import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable, Injector } from "@angular/core";
import { Observable, of } from "rxjs";
import { tap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { ApiResponse } from "../shared/models/api-response.model";
import { CompanyReference, GeminiUsersPage, GeminiUsersQuery, GarsUserProfile } from "./common-service.models";
import { AuthService } from "../services/auth.service";

@Injectable({
    providedIn: 'root'
})
export class CommonService {
    http = inject(HttpClient);
    private readonly injector = inject(Injector);
    private _authService?: AuthService;
    get authService(): AuthService {
        if (!this._authService) {
            this._authService = this.injector.get(AuthService);
        }
        return this._authService;
    }
    baseURL = environment.apiUrl + environment.version;
    readonly profileURL = `${environment.apiUrl}${environment.version}/gars-users/profile`;

    getCompanyHierachy(): Observable<ApiResponse<CompanyReference[]>> {
        return this.http.get<ApiResponse<CompanyReference[]>>(this.baseURL + '/gemini/company-references')
    }

    getGeminiUsers(query: GeminiUsersQuery = {}): Observable<ApiResponse<GeminiUsersPage>> {
        let params = new HttpParams();
        const pushArray = (key: string, values: string[] = []) => {
            values.forEach(v => { params = params.append(key, v); });
        };
        pushArray('region', query.region);
        pushArray('country', query.country);
        const compCodes = query.companyCodes ?? query.companyCode;
        if (compCodes && compCodes.length > 0) {
            params = params.set('companyCodes', compCodes.join(','));
            pushArray('companyCode', compCodes);
        }
        pushArray('licenseType', query.licenseType);
        if (query.active !== null && query.active !== undefined) {
            params = params.set('active', String(query.active));
        }
        params = params.set('page', String(query.page ?? 0));
        params = params.set('size', String(query.size ?? 10));
        if (query.sortBy) params = params.set('sortBy', query.sortBy);
        params = params.set('sortDir', query.sortDir ?? 'asc');
        return this.http.get<ApiResponse<GeminiUsersPage>>(this.baseURL + '/gemini-users', { params });
    }

    /**
     * Fetch GARS user profile from `/api/v1/gars-users/profile` and synchronize
     * permissions and data scope in AuthService.
     * Uses Stale-While-Revalidate: Returns cached profile data immediately if available,
     * while silently refreshing from the server in the background.
     */
    getUserProfile(forceRefresh: boolean = false): Observable<ApiResponse<GarsUserProfile> | GarsUserProfile> {
        if (!forceRefresh) {
            const cached = this.authService.getStoredUserProfile() || this.authService.userProfile();
            if (cached) {
                this.authService.applyProfileData(cached);
                this.authService.fetchUserProfile(0, true).subscribe();
                return of(cached);
            }
        }
        return this.http.get<ApiResponse<GarsUserProfile> | GarsUserProfile>(this.profileURL).pipe(
            tap((res: any) => {
                const profile = (res && typeof res === 'object' && 'data' in res ? res.data : res) as GarsUserProfile;
                if (profile) {
                    this.authService.applyProfileData(profile);
                }
            })
        );
    }
}

